import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, GitBranch } from 'lucide-react';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_DATA = {
  count: 5,
  items: [
    { id: '1', name: 'Update fire evacuation plan', due_date: new Date(Date.now() - 604800000).toISOString().split('T')[0], daysOverdue: 7, project_id: '1', projects: { name: 'Site Alpha' }, isWorkflowTask: false },
    { id: '2', name: 'Complete PAT testing', due_date: new Date(Date.now() - 432000000).toISOString().split('T')[0], daysOverdue: 5, project_id: '2', projects: { name: 'Main Office' }, isWorkflowTask: false },
    { id: '3', name: 'Review first aid supplies', due_date: new Date(Date.now() - 259200000).toISOString().split('T')[0], daysOverdue: 3, project_id: '1', projects: { name: 'Site Alpha' }, isWorkflowTask: true },
  ],
};

export function OverdueTasksTile({ filter, useMockData }) {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [data, setData] = useState({ count: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);

  // Observe container size changes
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (useMockData) {
      setData(MOCK_DATA);
      setLoading(false);
      return;
    }

    if (!company?.id) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        const today = new Date().toISOString().split('T')[0];

        // Fetch ALL overdue tasks in the company/space (not just current user's)
        let taskQuery = supabase
          .from('tasks')
          .select(`
            id,
            name,
            subtasks,
            due_date,
            assigned_to,
            project_id,
            projects!inner(name, company_id)
          `, { count: 'exact' })
          .eq('projects.company_id', company.id)
          .lt('due_date', today)
          .order('due_date', { ascending: true })
          .limit(20);

        if (filter?.spaceId) {
          taskQuery = taskQuery.eq('project_id', filter.spaceId);
        }

        const { data: tasks, error } = await taskQuery;
        if (error) throw error;

        // Filter to only incomplete tasks (not completed)
        const activeTasks = (tasks || []).filter(task => {
          // Check if not completed (all subtasks done = completed)
          if (!task.subtasks || task.subtasks.length === 0) return true;
          const completedCount = task.subtasks.filter(st => st.completed).length;
          return completedCount < task.subtasks.length;
        });

        const regularItems = activeTasks.map(t => ({
          ...t,
          daysOverdue: Math.ceil((new Date() - new Date(t.due_date)) / (1000 * 60 * 60 * 24)),
          isWorkflowTask: false,
        }));

        // Fetch overdue workflow task runs
        let workflowQuery = supabase
          .from('checklists_workflow_runs')
          .select(`
            id,
            run_name,
            project_id,
            status,
            started_by,
            workflows(id, name, tasks),
            projects(name)
          `)
          .eq('status', 'in_progress');

        if (filter?.spaceId) {
          workflowQuery = workflowQuery.eq('project_id', filter.spaceId);
        }

        const { data: workflowRuns, error: runsError } = await workflowQuery;
        if (runsError) throw runsError;

        let workflowItems = [];

        if (workflowRuns && workflowRuns.length > 0) {
          const runIds = workflowRuns.map(r => r.id);

          const { data: taskRuns, error: taskRunsError } = await supabase
            .from('checklists_workflow_task_runs')
            .select('*')
            .in('workflow_run_id', runIds)
            .neq('status', 'completed')
            .lt('due_date', today);

          if (taskRunsError) throw taskRunsError;

          // Show ALL overdue workflow tasks in company/space (not filtered by user)
          workflowItems = (taskRuns || []).map(taskRun => {
            const workflowRun = workflowRuns.find(r => r.id === taskRun.workflow_run_id);
            const workflow = workflowRun?.workflows;
            const workflowTaskDef = (workflow?.tasks || []).find(t => String(t.id) === taskRun.task_id);

            return {
              id: taskRun.id,
              name: workflowTaskDef?.formTitle || workflowTaskDef?.name || `Task ${taskRun.task_order + 1}`,
              due_date: taskRun.due_date,
              project_id: workflowRun?.project_id,
              projects: workflowRun?.projects,
              workflow_run_id: taskRun.workflow_run_id,
              workflow_run_name: workflowRun?.run_name,
              workflow_name: workflow?.name,
              isWorkflowTask: true,
              daysOverdue: Math.ceil((new Date() - new Date(taskRun.due_date)) / (1000 * 60 * 60 * 24)),
            };
          });
        }

        // Combine and sort by most overdue first
        const allItems = [...regularItems, ...workflowItems]
          .sort((a, b) => b.daysOverdue - a.daysOverdue)
          .slice(0, 10);

        setData({ count: allItems.length, items: allItems });
      } catch (err) {
        console.error('Failed to load overdue tasks:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [company?.id, filter?.spaceId, useMockData]);

  // Determine layout based on dimensions
  const { width, height } = dimensions;
  const isXS = width < 150 || height < 100;
  const isSmall = width < 200 || height < 150;
  const isWide = width >= 450; // Wide enough for table columns
  const isLarge = width >= 350 && height >= 300;

  // Calculate how many items to show
  const getVisibleCount = () => {
    if (isXS) return 0;
    if (isSmall) return 0;
    const itemHeight = 48;
    const headerHeight = isLarge ? 50 : 40;
    const availableHeight = height - headerHeight - 20;
    return Math.max(1, Math.floor(availableHeight / itemHeight));
  };

  const handleTaskClick = (task) => {
    if (task.isWorkflowTask && task.workflow_run_id) {
      navigate(`/space/${task.project_id}/workflow-run/${task.workflow_run_id}`);
    } else {
      navigate(`/space/${task.project_id}/tasks`);
    }
  };

  if (loading) {
    return (
      <div ref={containerRef} className="flex items-center justify-center h-full">
        <div className="animate-pulse text-center">
          <div className="w-12 h-12 rounded-full bg-gray-200 mx-auto mb-2" />
          <div className="h-3 w-16 bg-gray-200 rounded mx-auto" />
        </div>
      </div>
    );
  }

  // All clear state
  if (data.count === 0) {
    return (
      <div ref={containerRef} className="flex flex-col items-center justify-center h-full text-center overflow-visible">
        <div className="bg-green-100 p-2.5 rounded-full mb-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
        </div>
        <p className="text-sm font-medium text-green-700">All Caught Up</p>
        {!isXS && <p className="text-xs text-gray-500">No overdue tasks</p>}
      </div>
    );
  }

  const visibleItems = data.items.slice(0, getVisibleCount());
  const remainingCount = data.count - visibleItems.length;

  // XS Layout: Just count with alert
  if (isXS) {
    return (
      <div ref={containerRef} className="flex items-center justify-center h-full overflow-visible">
        <div className="text-center">
          <div className="text-3xl font-bold text-red-600 leading-none">
            {data.count}
          </div>
          <p className="text-xs text-red-500 mt-1">overdue</p>
        </div>
      </div>
    );
  }

  // Small Layout: Count with icon
  if (isSmall) {
    return (
      <div ref={containerRef} className="flex flex-col items-center justify-center h-full text-center p-2 overflow-visible">
        <div className="bg-red-100 p-2 rounded-full mb-1.5">
          <AlertTriangle className="h-5 w-5 text-red-600" />
        </div>
        <p className="text-2xl font-bold text-red-600">{data.count}</p>
        <p className="text-xs text-gray-600">overdue</p>
        {data.items[0] && (
          <p className="text-[10px] text-gray-500 mt-1">
            Oldest: {data.items[0].daysOverdue}d ago
          </p>
        )}
      </div>
    );
  }

  // Wide/Large Layout: Table view with columns
  if (isWide || isLarge) {
    return (
      <div ref={containerRef} className="flex flex-col h-full p-2">
        {/* Table Header */}
        <div className={cn(
          "grid gap-2 pb-2 border-b text-xs font-medium text-gray-500",
          isWide ? "grid-cols-[1fr_70px_120px_100px]" : "grid-cols-[1fr_70px_100px]"
        )}>
          <div className="flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
            <span>Name</span>
          </div>
          <div>Overdue</div>
          {isWide && <div>Workflow Run</div>}
          <div>Workflow</div>
        </div>

        {/* Table Body */}
        <div className="flex-1 overflow-y-auto">
          {visibleItems.map((task) => (
            <div
              key={task.id}
              onClick={() => handleTaskClick(task)}
              className={cn(
                "grid gap-2 py-2 border-b border-red-100 hover:bg-red-50 cursor-pointer transition-colors items-center text-sm",
                isWide ? "grid-cols-[1fr_70px_120px_100px]" : "grid-cols-[1fr_70px_100px]"
              )}
            >
              {/* Name */}
              <div className="flex items-center gap-2 min-w-0">
                {task.isWorkflowTask ? (
                  <GitBranch className="h-4 w-4 text-blue-500 shrink-0" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
                )}
                <span className="truncate font-medium">{task.name}</span>
              </div>

              {/* Days Overdue */}
              <div className="text-xs text-red-600 font-medium">
                {task.daysOverdue}d ago
              </div>

              {/* Workflow Run (wide only) */}
              {isWide && (
                <div className="text-xs text-gray-600 truncate">
                  {task.isWorkflowTask ? task.workflow_run_name : '-'}
                </div>
              )}

              {/* Workflow/Space */}
              <div className="text-xs text-purple-600 truncate">
                {task.isWorkflowTask ? task.workflow_name : task.projects?.name}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        {remainingCount > 0 && (
          <button
            onClick={() => navigate('/tasks')}
            className="mt-2 pt-2 border-t text-center text-sm text-red-600 hover:text-red-700"
          >
            +{remainingCount} more
          </button>
        )}
      </div>
    );
  }

  // Default/Medium Layout
  return (
    <div ref={containerRef} className="flex flex-col h-full p-2">
      {/* Header badge */}
      <div className="flex items-center justify-between mb-2">
        <Badge variant="destructive" className="text-sm">
          {data.count} Overdue
        </Badge>
      </div>

      {/* List */}
      <div className="flex-1 space-y-1 overflow-hidden">
        {visibleItems.map((task) => (
          <div
            key={task.id}
            onClick={() => handleTaskClick(task)}
            className="flex items-center justify-between p-1.5 rounded-lg hover:bg-red-50 cursor-pointer transition-colors border border-red-100"
          >
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              {task.isWorkflowTask && (
                <GitBranch className="h-3.5 w-3.5 text-blue-500 shrink-0" />
              )}
              <div className="min-w-0">
                <p className="text-xs font-medium truncate">{task.name}</p>
                <p className="text-[10px] text-gray-500 truncate">
                  {task.isWorkflowTask ? task.workflow_name : task.projects?.name}
                </p>
              </div>
            </div>
            <span className="text-[10px] text-red-600 shrink-0 ml-1">
              {task.daysOverdue}d
            </span>
          </div>
        ))}
      </div>

      {/* More link */}
      {remainingCount > 0 && (
        <button
          onClick={() => navigate('/tasks')}
          className="text-xs text-red-600 hover:text-red-700 pt-1"
        >
          +{remainingCount} more
        </button>
      )}
    </div>
  );
}
